import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { User } from "@/types";
import { getStorageItem, setStorageItem, removeStorageItem } from "@/lib/utils";

interface AuthState {
  user: User | null;
  userProfile: any | null; // Convex user profile
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  permissions: string[];
}

const initialState: AuthState = {
  user: getStorageItem("user", null),
  userProfile: getStorageItem("userProfile", null),
  isAuthenticated: false, // Will be set by Clerk
  isLoading: false,
  error: null,
  permissions: getStorageItem("permissions", []),
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUserProfile: (state, action: PayloadAction<any>) => {
      state.userProfile = action.payload;
      state.isAuthenticated = !!action.payload;
      setStorageItem("userProfile", action.payload);
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      setStorageItem("user", action.payload);
    },
    setPermissions: (state, action: PayloadAction<string[]>) => {
      state.permissions = action.payload;
      setStorageItem("permissions", action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    logout: (state) => {
      state.user = null;
      state.userProfile = null;
      state.isAuthenticated = false;
      state.permissions = [];
      state.error = null;

      // Clear localStorage
      removeStorageItem("user");
      removeStorageItem("userProfile");
      removeStorageItem("permissions");
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        setStorageItem("user", state.user);
      }
    },
    clearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  setUserProfile,
  setUser,
  setPermissions,
  setLoading,
  setError,
  logout,
  updateUser,
  clearError,
} = authSlice.actions;

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectUserProfile = (state: { auth: AuthState }) => state.auth.userProfile;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.isLoading;
export const selectAuthError = (state: { auth: AuthState }) => state.auth.error;
export const selectPermissions = (state: { auth: AuthState }) => state.auth.permissions;

// Permission checker
export const selectHasPermission = (permission: string) => (state: { auth: AuthState }) => {
  return (
    state.auth.permissions.includes(permission) || state.auth.user?.role === "owner"
  );
};

export default authSlice.reducer;
